import React, { useState, useEffect } from 'react';
import { hapticFeedback } from '../../../utils/telegram';
import { useLanguage } from '../../../contexts/LanguageContext';
import FullscreenButton from '../FullscreenButton/FullscreenButton';
import LanguageSelector from '../LanguageSelector/LanguageSelector';
import './Settings.css';
import { useConfig } from '../../../hooks/useConfig';

const Settings = ({ onContactSupport }) => {
  const [showSettings, setShowSettings] = useState(false);
  const { t } = useLanguage();
  const config = useConfig();
  // Add/remove class to body when settings panel opens/closes
  useEffect(() => {
    if (showSettings) {
      document.body.classList.add('settings-open');
    } else {
      document.body.classList.remove('settings-open');
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove('settings-open');
    };
  }, [showSettings]);

  const handleSettingsClick = () => {
    hapticFeedback.light();
    setShowSettings(!showSettings);
  };

  const handleCloseSettings = () => {
    hapticFeedback.light();
    setShowSettings(false);
  };

  const handleContactSupport = () => {
    hapticFeedback?.medium();
    // Call the external handler if provided
    if (onContactSupport) {
      onContactSupport();
    } else {
      // Default behavior
      console.log('Contact support clicked', config.supportLink);
      window.open(config.supportLink, '_blank');
    }
  };

  return (
    <div className="settings-wrapper">
      <button className="icon-button settings" onClick={() => {
        hapticFeedback.light();
        handleSettingsClick();
      }}>
        <svg className="settings-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M19.14 12.94C19.18 12.64 19.2 12.33 19.2 12C19.2 11.68 19.18 11.36 19.14 11.06L21.16 9.48C21.34 9.34 21.39 9.07 21.28 8.87L19.36 5.55C19.24 5.33 18.99 5.26 18.77 5.33L16.38 6.29C15.88 5.91 15.35 5.59 14.76 5.35L14.4 2.81C14.36 2.57 14.16 2.4 13.92 2.4H10.08C9.84 2.4 9.65 2.57 9.61 2.81L9.25 5.35C8.66 5.59 8.12 5.92 7.63 6.29L5.24 5.33C5.02 5.25 4.77 5.33 4.65 5.55L2.74 8.87C2.62 9.08 2.66 9.34 2.86 9.48L4.88 11.06C4.84 11.36 4.8 11.69 4.8 12C4.8 12.31 4.82 12.64 4.86 12.94L2.84 14.52C2.66 14.66 2.61 14.93 2.72 15.13L4.64 18.45C4.76 18.67 5.01 18.74 5.23 18.67L7.62 17.71C8.12 18.09 8.65 18.41 9.24 18.65L9.6 21.19C9.65 21.43 9.84 21.6 10.08 21.6H13.92C14.16 21.6 14.36 21.43 14.39 21.19L14.75 18.65C15.34 18.41 15.88 18.09 16.37 17.71L18.76 18.67C18.98 18.75 19.23 18.67 19.35 18.45L21.27 15.13C21.39 14.91 21.34 14.66 21.15 14.52L19.14 12.94ZM12 15.6C10.02 15.6 8.4 13.98 8.4 12C8.4 10.02 10.02 8.4 12 8.4C13.98 8.4 15.6 10.02 15.6 12C15.6 13.98 13.98 15.6 12 15.6Z" fill="currentColor" />
        </svg>
      </button>

      {showSettings && (
        <>
          <div className="backdrop-overlay" onClick={() => {
            hapticFeedback.light();
            handleCloseSettings();
          }}></div>
          <div className="settings-panel">
            <div className="settings-header">
              <h3>{t('settings')}</h3>
              <button className="close-button" onClick={() => {
                hapticFeedback.light();
                handleCloseSettings();
              }}>
                ✕
              </button>
            </div>
            <div className="settings-divider"></div>
            <div className="settings-content">
              {/* Language Section */}
              <div className="settings-section">
                <div className="settings-section-title">{t('language')}</div>
                <div className="settings-option">
                  <LanguageSelector />
                </div>
              </div>

              <div className="settings-divider"></div>

              {/* Fullscreen Button Section */}
              <div className="settings-section">
                <div className="settings-section-title">{t('display')}</div>
                <div className="settings-option">
                  <div className="settings-option-info">
                    <div className="settings-option-label">{t('fullscreenMode')}</div>
                    <div className="settings-option-description">
                      {t('fullscreenModeDesc')}
                    </div>
                  </div>
                  <div className="settings-option-control">
                    <FullscreenButton showLabel={false} className="settings-fullscreen-btn" />
                  </div>
                </div>
              </div>

              <div className="settings-divider"></div>

              {/* Website Section */}
              <div className="settings-section">
                <div className="settings-section-title">{t('information')}</div>
                {config.socialLink &&
                  <>
                    <p className="settings-description">
                      {t('visitOfficialWebsite')}
                    </p>

                    <a href={config.socialLink} className="settings-website-link" target="_blank" rel="noopener noreferrer">
                      {config.socialLink}
                    </a>
                  </>}

                <p className="settings-support-text">
                  {t('supportContact')}
                </p>
              </div>
            </div>
            <div className="settings-footer">
              <button className="support-button" onClick={() => {
                handleContactSupport();
              }}>
                {t('technicalSupport')}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Settings; 